'use client';

import { useEffect, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Shield } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';

interface AcceptanceCheck {
  data: {
    richiesta: boolean;
    policy?: { id: string; versione: string; testo: string };
  };
}

/**
 * Modale di accettazione privacy policy mostrato globalmente quando l'utente
 * non ha ancora accettato la versione attiva del tenant.
 */
export function PolicyAcceptanceDialog() {
  const utente = useAuthStore((s) => s.utente);
  const [accepting, setAccepting] = useState(false);
  const [show, setShow] = useState(false);

  const { data, refetch } = useQuery<AcceptanceCheck>({
    queryKey: ['gdpr-policy-check', utente?.id],
    queryFn:  () => api.get('/gdpr/privacy-policy/check-accettazione').then((r) => r.data),
    enabled:  !!utente?.id,
    staleTime: 60_000,
  });

  useEffect(() => {
    setShow(!!data?.data.richiesta);
  }, [data]);

  const acceptMutation = useMutation({
    mutationFn: (policyId: string) => api.post('/gdpr/privacy-policy/accetta', { policy_id: policyId }),
    onSuccess: () => {
      toast.success('Informativa accettata');
      setShow(false);
      void refetch();
    },
    onError: (e: unknown) =>
      toast.error((e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Errore'),
  });

  if (!data?.data.richiesta || !data.data.policy) return null;
  const policy = data.data.policy;

  return (
    <Dialog open={show} onOpenChange={(v) => { if (!v && !accepting) return; if (accepting) setShow(v); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="size-5 text-blue-600" /> Informativa privacy — versione {policy.versione}
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto py-3 px-1">
          <pre className="text-xs whitespace-pre-wrap font-sans leading-relaxed">{policy.testo}</pre>
        </div>
        <DialogFooter>
          <Button
            onClick={() => { setAccepting(true); acceptMutation.mutate(policy.id); }}
            disabled={acceptMutation.isPending}
            className="w-full sm:w-auto"
          >
            {acceptMutation.isPending ? 'Salvataggio…' : 'Ho letto e accetto'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
